import React, { useState, useEffect } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { GRANTABLE_DEPARTMENT_SCOPES } from "./departmentScopes";
import { normalizeApiError, formatErrorMessage } from "../../utils/apiErrors";
import "./AccessManagerPanel.css";

/**
 * DepartmentDialog — Radix dialog serving both create and edit modes.
 *
 * Create mode opens with empty name, empty description, and all
 * GRANTABLE_DEPARTMENT_SCOPES unchecked. Edit mode pre-populates from
 * the `department` prop.
 *
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is visible.
 * @param {"create"|"edit"} props.mode - Dialog mode.
 * @param {{ id: number, name: string, description: string|null, scopes: string[], member_count: number }|null} props.department - Required in edit mode.
 * @param {() => void} props.onClose - Called when the dialog should close.
 * @param {(payload: { name: string, description: string, scopes: string[] }) => Promise<void>} props.onSubmit - Called on form submission.
 */
export default function DepartmentDialog({ open, mode, department, onClose, onSubmit }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [checkedScopes, setCheckedScopes] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // Reset all fields when dialog opens/closes so cancel discards entered values.
  // Matches EditUserDialog's reset pattern.
  useEffect(() => {
    if (open) {
      if (mode === "edit" && department) {
        setName(department.name || "");
        setDescription(department.description || "");
        setCheckedScopes(
          Array.isArray(department.scopes)
            ? department.scopes.filter((s) =>
                GRANTABLE_DEPARTMENT_SCOPES.includes(s)
              )
            : []
        );
      } else {
        setName("");
        setDescription("");
        setCheckedScopes([]);
      }
      setError(null);
    } else {
      setName("");
      setDescription("");
      setCheckedScopes([]);
      setError(null);
      setSubmitting(false);
    }
  }, [open, mode, department]);

  const handleScopeToggle = (scope) => {
    setCheckedScopes((prev) =>
      prev.includes(scope)
        ? prev.filter((s) => s !== scope)
        : [...prev, scope]
    );
  };

  const trimmedName = name.trim();
  const canSubmit = trimmedName.length > 0 && checkedScopes.length > 0 && !submitting;

  // Determine if the checked set differs from the department's original set (edit mode)
  const scopesChanged = (() => {
    if (mode !== "edit" || !department || !Array.isArray(department.scopes)) return false;
    const original = new Set(department.scopes);
    const current = new Set(checkedScopes);
    if (original.size !== current.size) return true;
    for (const s of current) {
      if (!original.has(s)) return true;
    }
    return false;
  })();

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setSubmitting(true);
    setError(null);

    try {
      await onSubmit({
        name: trimmedName,
        description,
        scopes: checkedScopes,
      });
    } catch (err) {
      setError(formatErrorMessage(normalizeApiError(err)));
    } finally {
      setSubmitting(false);
    }
  };

  const title = mode === "edit" ? "Edit Department" : "Create Department";
  const submitLabel = mode === "edit"
    ? submitting ? "Saving…" : "Save"
    : submitting ? "Creating…" : "Create";

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="access-manager-dialog__overlay" />
        <Dialog.Content className="access-manager-dialog__content">
          <Dialog.Title className="access-manager-dialog__title">
            {title}
          </Dialog.Title>
          <Dialog.Description className="access-manager-dialog__description">
            {mode === "edit"
              ? <>Modify settings for <strong>{department?.name}</strong></>
              : "Create a new department with a name and scope selection."}
          </Dialog.Description>

          {/* Name field */}
          <div className="access-manager-dialog__field">
            <label
              htmlFor="department-dialog-name"
              className="access-manager-dialog__notes-label"
            >
              Name <span aria-hidden="true">*</span>
            </label>
            <input
              id="department-dialog-name"
              type="text"
              className="access-manager-dialog__email-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Department name"
              required
              disabled={submitting}
              autoComplete="off"
            />
          </div>

          {/* Description field */}
          <div className="access-manager-dialog__field">
            <label
              htmlFor="department-dialog-description"
              className="access-manager-dialog__notes-label"
            >
              Description
            </label>
            <textarea
              id="department-dialog-description"
              className="access-manager-dialog__notes-input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description"
              disabled={submitting}
              rows={2}
            />
          </div>

          {/* Scope checkboxes */}
          <fieldset className="department-dialog__scope-list" disabled={submitting}>
            <legend className="access-manager-dialog__notes-label">
              Scopes this department grants <span aria-hidden="true">*</span>
            </legend>
            {GRANTABLE_DEPARTMENT_SCOPES.map((scope) => (
              <label key={scope} className="department-dialog__scope-item">
                <input
                  type="checkbox"
                  className="department-dialog__scope-checkbox"
                  checked={checkedScopes.includes(scope)}
                  onChange={() => handleScopeToggle(scope)}
                  disabled={submitting}
                />
                {scope}
              </label>
            ))}
          </fieldset>

          {/* Impact notice in edit mode when scopes changed */}
          {mode === "edit" && scopesChanged && department && (
            <div className="department-dialog__impact-notice">
              This change applies to all {department.member_count} current{" "}
              {department.member_count === 1 ? "member" : "members"} of this department.
            </div>
          )}

          {/* Error region */}
          {error && (
            <div className="access-manager-panel__error" role="alert">
              {error}
            </div>
          )}

          <div className="access-manager-dialog__actions">
            <Dialog.Close asChild>
              <button
                type="button"
                className="access-manager-dialog__btn access-manager-dialog__btn--cancel"
                disabled={submitting}
              >
                Cancel
              </button>
            </Dialog.Close>
            <button
              type="button"
              className="access-manager-dialog__btn access-manager-dialog__btn--confirm"
              onClick={handleSubmit}
              disabled={!canSubmit}
            >
              {submitLabel}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
